import React from 'react';
import { observer } from 'mobx-react-lite';
import { useContext, useEffect, useRef, useState } from 'react';
import { Print } from '@material-ui/icons';
import ReactToPrint from 'react-to-print';
import { Box, Button, Divider, Grid, Typography } from '@material-ui/core';
import CancelIcon from '@material-ui/icons/Cancel';

import { format } from 'date-fns';

import {
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from '@mui/material';
import {
    BillStatus,
    IBill,
    IdBill,
    IdLeaseAgreement,
    ITransactionLog,
    IUserBio,
} from 'realhaus-sdk';
import { RightDrawer } from '../../uiComponents/rightDrawer';

import { formatAddress } from '../../../utils/address';
import { RootStoreContext } from '../../../global/storeContext';
import { RealhausLogo } from '../../../layouts/main/logo';
import StringUtils from '../../../utils/string';
import { useStyles } from './billStyles';
import { Timestamp } from 'firebase/firestore';

export const BillsReportComponent: React.FC<{
    open: boolean;
    closeView: () => void;
    leaseAgreement: IdLeaseAgreement;
}> = observer(({ open, closeView, leaseAgreement }) => {
    const printRef = useRef(null);

    return (
        <>
            <RightDrawer isOpen={open} onOpen={() => {}} onClose={closeView}>
                <Grid container direction='column' spacing={0}>
                    <Grid
                        container
                        item
                        spacing={0}
                        justifyContent='space-between'
                        style={{ width: '100%', display: 'flex' }}
                    >
                        <Grid item>
                            <ReactToPrint
                                pageStyle={`@media print{
                              @page{
                                        margin:20mm 10mm 20mm 10mm;
                              }
                            }`}
                                trigger={() => (
                                    <Button>
                                        <Print /> Download PDF
                                    </Button>
                                )}
                                content={() => printRef.current}
                                documentTitle='Realhaus - Bill Payment Report'
                            />
                        </Grid>
                        <Grid item>
                            <Button
                                style={{ borderRadius: '0', color: 'black' }}
                                onClick={closeView}
                            >
                                <CancelIcon />
                            </Button>
                        </Grid>
                    </Grid>
                    <BillsReportView ref={printRef} leaseAgreement={leaseAgreement} />
                    <Grid item></Grid>
                </Grid>
            </RightDrawer>
        </>
    );
});

export const BillsReportView = observer(
    React.forwardRef(
        (
            { leaseAgreement }: { leaseAgreement: IdLeaseAgreement },
            ref: React.Ref<HTMLDivElement>,
        ) => {
            const { leaseStore, userStore, walletStore } = useContext(RootStoreContext);
            const [paidBills, setPaidBills] = useState<IdBill[]>([]);
            const [transactions] = useState<Map<String, ITransactionLog>>(new Map());
            const [tenantInfos] = useState<Map<String, IUserBio>>(new Map());
            const classes = useStyles();

            const getUpdateTenantInfo = async (tId: string) => {
                const info = await userStore.getUserProfile(tId);
                if (!info) return;
                tenantInfos?.set(tId, info);
            };

            const getUpdateTransactions = async (trxId: string) => {
                const t = await walletStore.getTransactionLog(trxId);
                if (!t || transactions.has(trxId)) return;

                transactions.set(trxId, t);
            };
            useEffect(() => {
                const init = async () => {
                    const bills = await leaseStore.getLeaseBills(leaseAgreement.id);
                    const filteredBills = bills
                        .filter((b) => b.status === BillStatus.PAID)
                        .sort((a, b) => a.dueDate - b.dueDate);

                    setPaidBills(filteredBills);

                    await Promise.allSettled([
                        ...leaseAgreement.tenantIds.map(async (t) => {
                            if (!tenantInfos.has(t)) {
                                return await getUpdateTenantInfo(t);
                            }
                            return Promise.resolve();
                        }),

                        ...filteredBills.map(async (b) => {
                            if (b.trxLogRef && !transactions.has(b.trxLogRef)) {
                                return await getUpdateTransactions(b.trxLogRef);
                            }
                            return Promise.resolve();
                        }),
                    ]);
                };

                init();
            });

            const firebaseTSToDate = (ts: { seconds: number; nanoseconds: number }) => {
                const t = new Timestamp(ts.seconds, ts.nanoseconds);
                return t.toDate();
            };

            const getDatePaid = (bill: IBill) => {
                if (bill.datePaid) return format(bill.datePaid, 'PP');
                if ((bill as any).updatedAt)
                    return format(firebaseTSToDate((bill as any).updatedAt), 'PP');

                return '';
            };

            const paymentsComponent = () => {
                return (
                    <Grid item xs={12}>
                        <TableContainer>
                            <Table sx={{ minWidth: 650 }} aria-label='simple table'>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Date</TableCell>
                                        <TableCell>Reference</TableCell>
                                        <TableCell align='right'>Type</TableCell>
                                        <TableCell align='right'>Method</TableCell>
                                        <TableCell align='right'>Status</TableCell>
                                        <TableCell align='right'>Amount</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {paidBills.map((bill) => (
                                        <TableRow
                                            key={bill.id}
                                            sx={{
                                                '&:last-child td, &:last-child th': {
                                                    border: 0,
                                                },
                                            }}
                                        >
                                            <TableCell component='th' scope='row'>
                                                {getDatePaid(bill)}
                                            </TableCell>
                                            <TableCell>
                                                {bill.trxLogRef ?? 'OFF_PLATFORM'}
                                            </TableCell>
                                            <TableCell align='right'>
                                                {(bill.trxLogRef &&
                                                    transactions.get(bill.trxLogRef)?.meta
                                                        ?.paymentMethod) ??
                                                    'N/A'}
                                            </TableCell>
                                            <TableCell align='right'>
                                                {bill.type.replaceAll('_', ' ')}
                                            </TableCell>
                                            <TableCell align='right'>{bill.status}</TableCell>
                                            <TableCell align='right'>
                                                {StringUtils.asCurrency(bill.amount)}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Grid>
                );
            };
            return (
                <div ref={ref}>
                    <Grid container spacing={1} direction='row'>
                        <Grid item xs={12} spacing={1}>
                            <Typography variant='h6' align='right'>
                                <RealhausLogo />
                            </Typography>

                            <Typography variant='h6' align='center'>
                                Tenant Bill Payment Report
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Box
                                className={classes.cardParent}
                                style={{
                                    backgroundColor: 'transparent',
                                }}
                            >
                                <Grid container direction='row' spacing={2}>
                                    <Grid item xs={12}>
                                        <Typography variant='h6'>Property</Typography>
                                        <Divider style={{ marginTop: '5px' }} />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Typography variant='body1'>
                                            {formatAddress(leaseAgreement.listingInfo.address)}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Box>
                        </Grid>
                        <Grid item xs={6}>
                            <Box
                                className={classes.cardParent}
                                style={{
                                    backgroundColor: 'transparent',
                                }}
                            >
                                <Grid container direction='row' spacing={2}>
                                    <Grid item xs={12}>
                                        <Typography variant='h6'>Lease Info</Typography>
                                        <Divider style={{ marginTop: '5px' }} />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Stack direction={'row'} spacing={3}>
                                            <Typography variant='body1'>
                                                Move-In:{' '}
                                                <b>{format(leaseAgreement.moveinDate, 'PP')}</b>
                                            </Typography>
                                            <Typography variant='body1'>
                                                Move-Out:{' '}
                                                <b>{format(leaseAgreement.moveoutDate, 'PP')}</b>
                                            </Typography>
                                        </Stack>
                                    </Grid>
                                </Grid>
                            </Box>
                        </Grid>
                        <Grid item xs={12}>
                            <Box
                                className={classes.cardParent}
                                style={{
                                    backgroundColor: 'transparent',
                                }}
                            >
                                <Grid container direction='row' spacing={2}>
                                    <Grid item xs={12}>
                                        <Typography variant='h6'>Tenant(s)</Typography>
                                        <Divider style={{ marginTop: '5px' }} />
                                    </Grid>
                                    <Grid container item xs={12} spacing={1}>
                                        {Array.from(tenantInfos.values()).map((tenant, i) => (
                                            <Grid item key={`key-tenant-${i}`} xs={12}>
                                                <Typography variant='body1'>{`${tenant.firstname} ${tenant.lastname}`}</Typography>
                                            </Grid>
                                        ))}
                                        {leaseAgreement.occupants.map((occupant, i) => (
                                            <Grid item key={`key-occupant-${i}`} xs={12}>
                                                <Typography variant='body1'>{`${occupant.firstName} ${occupant.lastName}`}</Typography>
                                            </Grid>
                                        ))}
                                    </Grid>
                                </Grid>
                            </Box>
                        </Grid>
                        <Grid item xs={12}>
                            <Box
                                className={classes.cardParent}
                                style={{
                                    backgroundColor: 'transparent',
                                }}
                            >
                                <Grid container direction='row' spacing={2}>
                                    <Grid item xs={12}>
                                        <Typography variant='h6'>Payments</Typography>
                                        <Divider style={{ marginTop: '5px' }} />
                                    </Grid>
                                    {paymentsComponent()}
                                </Grid>
                            </Box>
                        </Grid>
                    </Grid>
                </div>
            );
        },
    ),
);
